import { useState } from "react";

/**
 * Adaptive photo frame for the "Hear a Bray" / "Hear a Moo" companion sections.
 *
 * — Preserves the original aspect ratio of ANY uploaded image (portrait or
 *   landscape) using `object-fit: contain`.
 * — Sits inside a soft cream/wood mat that gracefully handles empty space
 *   when the photo doesn't fill the frame in one dimension.
 * — Falls back to a peaceful pasture-toned placeholder when no photo is set,
 *   so the layout never breaks while the admin is still uploading.
 * — Fully responsive: caps to a comfortable max height on desktop, shrinks
 *   naturally on tablet/mobile.
 *
 * Do NOT place text over this element — the parent's grid layout handles the
 * split composition.
 */
export default function AdaptivePhoto({
  src,
  alt,
  tilt = "-0.5deg",
  frameColor = "#7E9268",
  placeholder = "photo coming soon",
  testId,
}) {
  const [failed, setFailed] = useState(false);
  const showImage = Boolean(src) && !failed;

  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const resolvedSrc = showImage && !src.startsWith("http") ? `${backendUrl}${src}` : src;

  return (
    <figure
      data-testid={testId}
      className="relative w-full mx-auto"
      style={{ transform: `rotate(${tilt})` }}
    >
      <div
        className="relative bg-[#F5EDDE] p-3 sm:p-4"
        style={{
          borderRadius: "3px",
          boxShadow:
            "0 1px 0 rgba(255,255,255,0.6) inset, 0 -1px 0 rgba(0,0,0,0.04) inset, 0 22px 30px -18px rgba(0,0,0,0.35), 0 2px 0 rgba(75,58,47,0.15)",
        }}
      >
        {/* Inner wood-tone frame — hosts the image at its native aspect ratio */}
        <div
          className="relative w-full flex items-center justify-center overflow-hidden"
          style={{
            background: "#F9F1E1",
            border: `6px solid ${frameColor}`,
            borderRadius: "2px",
            boxShadow:
              "inset 0 0 0 1px rgba(255,255,255,0.10), inset 0 0 24px rgba(0,0,0,0.20), 0 0 0 1px rgba(75,58,47,0.15)",
            // Cap the container so a very tall portrait doesn't push the row too far,
            // and give landscape shots a comfortable canvas. `object-fit: contain`
            // does the rest — no cropping, no distortion.
            maxHeight: "min(72vh, 560px)",
            minHeight: "260px",
            // When the frame is empty (no photo uploaded yet), reserve a natural
            // 4:3-ish canvas so the placeholder isn't stretched thin. Once a real
            // photo is uploaded the <img> takes over and this aspect is ignored.
            aspectRatio: showImage ? "auto" : "4 / 3",
          }}
        >
          {showImage ? (
            <img
              src={resolvedSrc}
              alt={alt || ""}
              onError={() => setFailed(true)}
              draggable="false"
              className="block max-w-full max-h-[min(72vh,560px)] w-auto h-auto select-none"
              style={{ objectFit: "contain" }}
            />
          ) : (
            <div className="flex flex-col items-center justify-center gap-3 py-16 text-center px-6">
              {/* Soft pasture wash + honey light behind the placeholder */}
              <span
                aria-hidden
                className="pointer-events-none absolute inset-0"
                style={{
                  background:
                    "radial-gradient(ellipse at 50% 100%, rgba(126,146,104,0.22), transparent 65%), radial-gradient(ellipse at 50% 0%, rgba(238,232,79,0.22), transparent 55%)",
                }}
              />
              <div className="relative text-5xl select-none" aria-hidden>
                📷
              </div>
              <p className="relative text-[11px] uppercase tracking-[0.35em] text-[#6D5A50]">
                {placeholder}
              </p>
            </div>
          )}
        </div>
      </div>
    </figure>
  );
}
